import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Divider
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { ApiRequest, formatCanadianPrice } from "GlobalFunctions";
import { useSnackbar } from "notistack";
import moment from "moment";
import AutocompleteField_su from "elements/AutocompleteField_su";


export default function AddProduct(params: any) {

  const { 
    open, 
    onClose, 
    subscription_id, 
    onProductAdded,
    isEditing = false,
    productData = null,
    subscription = null
  } = params;
  
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    product_name: "",
    product_sku: "",
    product_price: "",
    total_tax: 0,
    total_price: 0,
    shipping_id: "",
    shipping: {} as any,
    cycle_quantity: "1",
    cycle_definition: "m",
    next_payment_date: "",
    customInputs: [] as { name: string; value: string }[],
    shippingMethodChanged: false // <-- New property
  });

  // Store all original custom inputs, including empty ones
  const [allCustomInputs, setAllCustomInputs] = useState<{ name: string; value: string }[]>([]);
  // Store subscription-level custom inputs
  const [subscriptionCustomInputs, setSubscriptionCustomInputs] = useState<{ name: string; value: string }[]>([]);
  // Track original subscription custom inputs for comparison
  const [originalSubscriptionInputs, setOriginalSubscriptionInputs] = useState<{ name: string; value: string }[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [editShipping, setEditShipping] = useState(false); // <-- Local state for editing shipping

  // Track if the user has manually changed the date
  const manualDateChange = useRef(false);
  // Track if it's the initial load to prevent automatic calculation
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Initialize subscriptionData for shipping calculations and subscription custom inputs
  useEffect(() => {
    if (subscription) {
      // Load subscription-level custom inputs if available
      if (subscription.custom_inputs && Array.isArray(subscription.custom_inputs)) {
        // Store all subscription custom inputs, including empty ones
        setOriginalSubscriptionInputs(subscription.custom_inputs);
        
        // Filter out empty values for display
        const nonEmptySubscriptionInputs = subscription.custom_inputs.filter(
          (input: { name: string; value: string }) => input.value.trim() !== ""
        );
        setSubscriptionCustomInputs(nonEmptySubscriptionInputs);
      }
      
      // Create a properly formatted cart object for shipping calculation
      const cartData = {
        products: isEditing && productData ? 
          // When editing, use the current product data
          [{
            order_product_id: productData.subscription_product_id,
            product_name: productData.product_name,
            product_price: productData.product_price,
            product_quantity: 1,
            weight: getProductWeight(productData.custom_inputs),
            length: getProductDimension(productData.custom_inputs, 'length'),
            width: getProductDimension(productData.custom_inputs, 'width'),
            height: getProductDimension(productData.custom_inputs, 'height'),
            custom_inputs: productData.custom_inputs
          }] : 
          // When adding new, prepare empty product with default values
          [{
            order_product_id: 0,
            product_name: formData.product_name || "New Product",
            product_price: parseFloat(formData.product_price) || 0,
            product_quantity: 1,
            weight: 1, // Default weight
            length: 10, // Default dimensions
            width: 10,
            height: 10,
            custom_inputs: formData.customInputs
          }],
        client: {
          // Shipping address fields - use s_ prefix as expected by the API
          s_fname: subscription.client_name,
          s_lname: subscription.client_lastname,
          s_country: subscription.country_id,
          s_state: subscription.region_id,
          s_city_id: subscription.city_id,
          s_postal: subscription.postal_code,
          s_address: subscription.address || "",
          s_address2: subscription.address2 || "",
          email: subscription.email,
          phone: subscription.phone
        }
      };

      setSubscriptionData(cartData);
    }
  }, [subscription, productData, isEditing, formData.product_name, formData.product_price]);

  // Helper function to extract product weight from custom_inputs
  const getProductWeight = (custom_inputs: any) => {
    if (!custom_inputs || !Array.isArray(custom_inputs)) return 1;
    
    const weightInput = custom_inputs.find(input => input.name === 'weight');
    return weightInput && !isNaN(parseFloat(weightInput.value)) 
      ? parseFloat(weightInput.value) 
      : 1;
  };

  // Helper function to extract product dimensions from custom_inputs
  const getProductDimension = (custom_inputs: any, dimension: any) => {
    if (!custom_inputs || !Array.isArray(custom_inputs)) return 10;
    
    const dimensionInput = custom_inputs.find(input => input.name === dimension);
    return dimensionInput && !isNaN(parseFloat(dimensionInput.value)) 
      ? parseFloat(dimensionInput.value) 
      : 10;
  };

  // Initialize form with product data when editing
  useEffect(() => {
    // Reset initial load state when dialog opens
    setIsInitialLoad(true);
    
    if (isEditing && productData) {
      // Extract cycle information from frequency string
      let cycleQuantity = "1";
      let cycleDefinition = "m";
      
      // Parse the frequency string (e.g., "Monthly", "Every 2 Weeks")
      const frequencyStr = productData.frequency?.toLowerCase() || "";
      
      if (frequencyStr.includes("daily") || frequencyStr.includes("day")) {
        cycleDefinition = "d";
      } else if (frequencyStr.includes("weekly") || frequencyStr.includes("week")) {
        cycleDefinition = "w";
      } else if (frequencyStr.includes("monthly") || frequencyStr.includes("month")) {
        cycleDefinition = "m";
      } else if (frequencyStr.includes("yearly") || frequencyStr.includes("year")) {
        cycleDefinition = "y";
      }
      
      // Extract quantity if available (e.g., "Every 2 Weeks" → "2")
      const quantityMatch = frequencyStr.match(/\d+/);
      if (quantityMatch) {
        cycleQuantity = quantityMatch[0];
      }

      // Store all original custom inputs
      const originalCustomInputs = productData.custom_inputs || [];
      setAllCustomInputs(originalCustomInputs);

      // Filter out custom inputs with empty values for display purposes
      const nonEmptyCustomInputs = originalCustomInputs.filter(
        (input: { name: string; value: string }) => input.value.trim() !== ""
      );

      setFormData({
        product_name: productData.product_name || "",
        product_sku: productData.product_sku || "",
        product_price: productData.product_price?.toString() || "",
        total_tax: 0,
        total_price: 0,
        shipping: productData.shipping || {},
        shipping_id: productData.shipping_id || "",
        cycle_quantity: cycleQuantity,
        cycle_definition: cycleDefinition,
        next_payment_date: productData.next_payment_date || "",
        customInputs: nonEmptyCustomInputs,
        shippingMethodChanged: false // <-- Initialize new property
      });

      if (productData.shipping && Object.keys(productData.shipping).length > 0) {
        setEditShipping(false);
      } else {
        setEditShipping(true);
      }
      
      // Mark initial load as complete after setting form data
      setIsInitialLoad(false);
    } else {
      // Reset form when adding new product
      setFormData({
        product_name: "",
        product_sku: "",
        product_price: "",
        total_tax: 0,
        total_price: 0,
        shipping: {},
        shipping_id: "",
        cycle_quantity: "1",
        cycle_definition: "m",
        next_payment_date: "",
        customInputs: [],
        shippingMethodChanged: false // <-- Initialize new property
      });
      setAllCustomInputs([]);
      setEditShipping(false);
      
      // Mark initial load as complete
      setIsInitialLoad(false);
    }
  }, [isEditing, productData, open]);

  // Automatically update next_payment_date when cycle_quantity or cycle_definition changes
  useEffect(() => {
    // Only calculate if:
    // 1. Not during initial load
    // 2. Not a manual date change
    // 3. We have actual values to work with
    if (!isInitialLoad && !manualDateChange.current && formData.cycle_quantity && formData.cycle_definition) {
      let baseDate;
      
      // If editing and there's an existing next_payment_date, use it as base
      // Otherwise, use today's date for new products
      if (isEditing && productData?.next_payment_date) {
        baseDate = moment(productData.next_payment_date);
      } else {
        baseDate = moment();
      }
      
      let nextDate = baseDate.clone();
      const qty = parseInt(formData.cycle_quantity) || 1;
      
      switch (formData.cycle_definition) {
        case "d":
          nextDate = baseDate.add(qty, "days");
          break;
        case "w":
          nextDate = baseDate.add(qty, "weeks");
          break;
        case "m":
          nextDate = baseDate.add(qty, "months");
          break;
        case "y":
          nextDate = baseDate.add(qty, "years");
          break;
        default:
          break;
      }
      
      setFormData(prev => ({
        ...prev,
        next_payment_date: nextDate.format("YYYY-MM-DD")
      }));
    }
    // Reset manual change flag after auto-update
    if (!isInitialLoad) {
      manualDateChange.current = false;
    }
  }, [formData.cycle_quantity, formData.cycle_definition]);

  // Calculate tax and total price when product price changes
  useEffect(() => {
    const productPrice = Number(formData.product_price) || 0;
    const shippingPrice = Number(formData.shipping?.price) || 0;
    const totalProductPrice = productPrice + shippingPrice;
    const taxRate = subscription?.tax_rate ? Number(subscription.tax_rate) : 0;
    
    // Calculate tax amount based on tax rate percentage
    const taxAmount = totalProductPrice * (taxRate / 100);
    
    // Calculate total price (product price + tax)
    const totalPrice = totalProductPrice + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      total_tax: Number(taxAmount.toFixed(2)),
      total_price: Number(totalPrice.toFixed(2))
    }));
  }, [formData.product_price, subscription?.tax_rate, formData.shipping]);

  // const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
  //   const { name, value } = e.target;
  //   setFormData({
  //     ...formData,
  //     [name as string]: value
  //   });
  // };

  // Handler para el input manual de next_payment_date
  const handleNextPaymentDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    manualDateChange.current = true; // Mark as manual change
    setFormData({
      ...formData,
      next_payment_date: e.target.value
    });
  };

  const handleCustomInputChange = (index: number, value: string) => {
    const updatedInputs = [...formData.customInputs];
    updatedInputs[index].value = value;
    setFormData({
      ...formData,
      customInputs: updatedInputs
    });
  };

  // Handler for subscription custom input changes
  const handleSubscriptionCustomInputChange = (index: number, value: string) => {
    const updatedInputs = [...subscriptionCustomInputs];
    updatedInputs[index].value = value;
    setSubscriptionCustomInputs(updatedInputs);
  };

  const handleSubmit = () => {
    // Validate form data
    if (!formData.product_name) {
      enqueueSnackbar("Product name is required", { variant: "error" });
      return;
    }
    
    if (!formData.product_price || isNaN(Number(formData.product_price))) {
      enqueueSnackbar("Valid product price is required", { variant: "error" });
      return;
    }

    setLoading(true);
    
    const apiEndpoint = isEditing ? "/subscriptions/updateProduct" : "/subscriptions/addProduct";
    
    // Merge updated custom inputs with the original empty ones
    const mergedCustomInputs = [...allCustomInputs];
    
    // Update values of custom inputs that were edited
    formData.customInputs.forEach(updatedInput => {
      const existingIndex = mergedCustomInputs.findIndex(input => input.name === updatedInput.name);
      if (existingIndex >= 0) {
        mergedCustomInputs[existingIndex] = updatedInput;
      } else {
        mergedCustomInputs.push(updatedInput);
      }
    });
    
    // Merge updated subscription custom inputs with original ones
    const mergedSubscriptionInputs = [...originalSubscriptionInputs];
    
    // Update values of subscription custom inputs that were edited
    subscriptionCustomInputs.forEach(updatedInput => {
      const existingIndex = mergedSubscriptionInputs.findIndex(input => input.name === updatedInput.name);
      if (existingIndex >= 0) {
        mergedSubscriptionInputs[existingIndex] = updatedInput;
      } else {
        mergedSubscriptionInputs.push(updatedInput);
      }
    });
    
    // Create the request data with proper typing
    const { customInputs, ...otherFormData } = formData;
    const requestData: any = {
      subscription_id,
      ...otherFormData,
      product_price: Number(formData.product_price),
      custom_inputs: mergedCustomInputs, // Send all custom inputs, including empty ones
      subscription_custom_inputs: mergedSubscriptionInputs // Send updated subscription custom inputs
    };
    
    // Add product ID when editing
    if (isEditing && productData?.subscription_product_id) {
      requestData.subscription_product_id = productData.subscription_product_id;
    }
    
    ApiRequest({
      method: "POST",
      url: apiEndpoint,
      body: requestData,
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar(
            isEditing ? "Product updated successfully" : "Product added successfully", 
            { variant: "success" }
          );
          onProductAdded();
          onClose();
        } else {
          enqueueSnackbar(
            response.message || (isEditing ? "Failed to update product" : "Failed to add product"), 
            { variant: "error" }
          );
        }
      },
    });
  };

  const cycleDefinitions = [
    { value: "d", label: "Days" },
    { value: "w", label: "Weeks" },
    { value: "m", label: "Months" },
    { value: "y", label: "Years" },
  ];

  // Handler for all text and select field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
    // If the user changes cycle_quantity or cycle_definition, reset manual date change
    // and ensure we're not in initial load anymore
    if (name === "cycle_quantity" || name === "cycle_definition") {
      manualDateChange.current = false;
      setIsInitialLoad(false); // Ensure we're not in initial load state
    }
  };



  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className="DialogContainer">
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="dialog_container_title">
                <h2 className="dialog_title">
                 {isEditing ? "Edit Subscription" : "Add Subscription"}
                </h2>
            </div>
          </div>

          <div className="dialogFormContainer">
            <div className="subscription_product_dialog">
              <div className="form2">
                <div className="textfield_wrapper">
                  <InputLabel className="external_label">Product Name</InputLabel>
                  <TextField
                    name="product_name"
                    fullWidth
                    value={formData.product_name}
                    onChange={handleChange}
                    error={!formData.product_name && loading}
                    helperText={!formData.product_name && loading ? "Product name is required" : ""}
                    required
                    size="small"
                  />
                </div>
                
                <div className="input_row">
                  <div className="textfield_wrapper">
                    <InputLabel className="external_label">SKU (Optional)</InputLabel>
                    <TextField
                      name="product_sku"
                      fullWidth
                      value={formData.product_sku}
                      onChange={handleChange}
                      size="small"
                    />
                  </div>
                </div>
                
                <div className="input_row">
                  <div className="textfield_wrapper">
                    <InputLabel className="external_label">Every</InputLabel>
                    <TextField
                      name="cycle_quantity"
                      fullWidth
                      value={formData.cycle_quantity}
                      onChange={handleChange}
                      type="number"
                      inputProps={{ min: "1" }}
                      required
                      size="small"
                    />
                  </div>

                  <div className="textfield_wrapper">
                    <InputLabel className="external_label">Period</InputLabel>
                    <FormControl fullWidth>
                      <Select
                        name="cycle_definition"
                        value={formData.cycle_definition}
                        onChange={handleChange as any}
                        size="small"
                      >
                        {cycleDefinitions.map((cycle) => (
                          <MenuItem key={cycle.value} value={cycle.value}>
                            {cycle.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* Next Billing Cycle input (tipo fecha, después de Period) */}
                  <div className="textfield_wrapper">
                    <InputLabel className="external_label">Next Billing Cycle</InputLabel>
                    <TextField
                      name="next_payment_date"
                      fullWidth
                      value={formData.next_payment_date}
                      onChange={handleNextPaymentDateChange}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </div>

                  <div className="textfield_wrapper">
                    <InputLabel className="external_label">Price</InputLabel>
                    <TextField
                      name="product_price"
                      fullWidth
                      value={formData.product_price}
                      onChange={handleChange}
                      type="number"
                      InputProps={{
                        startAdornment: <span>$</span>,
                      }}
                      error={(!formData.product_price || isNaN(Number(formData.product_price))) && loading}
                      helperText={(!formData.product_price || isNaN(Number(formData.product_price))) && loading ? "Valid price is required" : ""}
                      required
                      size="small"
                    />
                  </div>

                </div>
                
                {/* Shipping method section */}
                <div className="textfield_wrapper">
                  <InputLabel className="external_label">Shipping Method</InputLabel>
                  {
                    Object.keys(formData.shipping).length > 0 &&
                    !editShipping && isEditing ? (
                      <div style={{ marginBottom: 16 }}>
                        <div>
                          {formData.shipping.rate_name} - ${formatCanadianPrice(formData.shipping.price)}
                        </div>
                        <a 
                          onClick={() => {
                            setEditShipping(true);
                            setFormData({
                              ...formData,
                              shippingMethodChanged: true
                            });
                          }}
                          className="addProductButton"
                        >
                          Edit shipping method
                        </a>
                      </div>
                    ) : (
                      <div className="textfield_wrapper">
                        {/* Show AutocompleteField_su when dialog is open AND (adding new product OR editing shipping) */}
                        {open && (!isEditing || editShipping) && (
                          <AutocompleteField_su
                            endpoint="/shipping/zones/getStoreShippingFromDashboard"
                            endpoint_method="post"
                            endpoint_params={{ 
                              store_id: localStorage.getItem("store_id"),
                              cart: subscriptionData,
                              returnType: "object",
                            }}
                            label="Shipping Rate"
                            mode="select"
                            value={formData.shipping_id}
                            onChange={(data: any) => {
                              setFormData({
                                ...formData,
                                shipping: data,
                                shipping_id: data ? data.value : ''
                              });
                            }}
                            fullWidth
                          />
                        )}
                      </div>
                    )
                  }
                </div>
                
                {formData.customInputs.length > 0 && (
                  <>
                      <h3 style={{ marginBottom: 8, marginTop: 16 }}>Product options</h3>
                    
                    {formData.customInputs.map((input, index) => (
                      <div className="label_grid" key={index}>
                        <div><InputLabel className="external_label">{input.name}</InputLabel></div>
                        <TextField
                          fullWidth
                          value={input.value}
                          onChange={(e) => handleCustomInputChange(index, e.target.value)}
                          size="small"
                        />
                      </div>
                    ))}
                  </>
                )}
                
                {/* Add subscription-level custom inputs section */}
                {subscriptionCustomInputs.length > 0 && (
                  <>
                    <h3 style={{ marginBottom: 8, marginTop: 16 }}>Custom Inputs</h3>
                    
                    {subscriptionCustomInputs.map((input, index) => (
                      <div className="label_grid" key={index}>
                        <div><InputLabel className="external_label">{input.name}</InputLabel></div>
                        <TextField
                          fullWidth
                          value={input.value}
                          onChange={(e) => handleSubscriptionCustomInputChange(index, e.target.value)}
                          size="small"
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div className="subscription_product_summary">
                
                <div className="summary_container">
                  
                  <h3 className=''>Order Summary</h3>

                  <div className="summary_item">
                    <div>
                      <h3>{formData.product_name}</h3>
                      <span className="summary_item_subtitle">
                        {(() => {
                          const cycle = cycleDefinitions.find(c => c.value === formData.cycle_definition);
                          if (!cycle) return null;
                          const quantity = Number(formData.cycle_quantity);
                          
                          if (quantity === 1) {
                            // Handle singular forms with proper adverbs
                            switch (formData.cycle_definition) {
                              case "d":
                                return "Billed daily";
                              case "w":
                                return "Billed weekly";
                              case "m":
                                return "Billed monthly";
                              case "y":
                                return "Billed yearly";
                              default:
                                return `Billed ${cycle.label.toLowerCase()}`;
                            }
                          } else {
                            return `Billed every ${quantity} ${cycle.label.toLowerCase()}`;
                          }
                        })()}
                      </span>
                    </div>
                    <span className="summary_item_price">${formatCanadianPrice(formData.product_price)}</span>
                  </div>

                  <div className="summary_item">
                    <div>
                      <h3>Shipping</h3>
                      <span className="summary_item_subtitle">
                        {formData.shipping?.rate_name || "No shipping selected"}
                      </span>
                    </div>
                    <span className="summary_item_price">${formatCanadianPrice(formData.shipping?.price || 0)}</span>
                  </div>


                </div>

                <Divider style={{  borderColor: "#ECEBFF" }} />

                <div className="summary_container summary_totals">
                 
                  <div className="summary_item">
                    <span className="summary_item_subtitle">Subtotal</span>
                    <span className="summary_item_price">${formatCanadianPrice(formData.product_price)}</span>
                  </div>

                  <div className="summary_item">
                    <span className="summary_item_subtitle">Tax</span>
                    <span className="summary_item_price">${formatCanadianPrice(formData.total_tax)}</span>
                  </div>

                  <div className="summary_item">
                    <h3>Total</h3>
                    <h3>${formatCanadianPrice(formData.total_price)}</h3>
                  </div>
                </div>

              </div>
            </div>
          </div>


        </div>
      </div>


        <div className="dialogButtons">
        <Button 
          onClick={onClose} 
          disabled={loading}
          className="button2_secondary"
        >
            Cancel
        </Button>

        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading}
          className="button2"
        >
          {loading ? (isEditing ? "Updating..." : "Adding...") : (isEditing ? "Update Product" : "Add Product")}
        </Button>
      </div>
    </Dialog>
  );
}
